import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { useGetAllQuestionQuery } from "../../../../../graphql/generated.tsx";
import { gql, useApolloClient } from "@apollo/client";
import Question from "../../../Components/QuestionCard.jsx";
import AdminLayout from "../../../Layout/index.jsx";
import NetworkIndicator from "../../../../Practice/Components/NetworkIndicator.jsx";
import { Button } from "flowbite-react";
import swal from "sweetalert";
import Loader from "../../../../../components/Loader.jsx";

// GraphQL mutation
const REQUEST_DELETION_MUTATION = gql`
  mutation requestDeletion(
    $typeOfDeletion: String!
    $payload: JSON!
    $reasonForDeletion: String!
    $requestedBy: String!
    $title: String!
  ) {
    requestDeletion(
      typeOfDeletion: $typeOfDeletion
      payload: $payload
      reasonForDeletion: $reasonForDeletion
      requestedBy: $requestedBy
      title: $title
    ) {
      id
      typeOfDeletion
      payload
      statusOfApproval
      requestedBy
      requestedAt
    }
  }
`;

const QuestionListing = () => {
  const { cat_id, course_id } = useParams();
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [catName, setCatName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [Isload, setIsload] = useState(true);

  const questionsPerPage = 10;

  const { data, loading, refetch } = useGetAllQuestionQuery({
    variables: {
      courseId: course_id,
      page: currentPage,
      limit: questionsPerPage,
      categoryId: String(cat_id),
    },
  });

  const apolloClient = useApolloClient();

  useEffect(() => {
    if (data?.getAllQuestion?.questions) {
      setCatName(data.getAllQuestion.questions?.[0]?.category?.name || "");
      setQuestions(data.getAllQuestion.questions);
    }
  }, [data]);

  useEffect(() => {
    setTimeout(() => {
      setIsload(false);
    }, 2000);
  }, []);

  // ✅ Search handler
  const handleSearch = async (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (!term.trim()) {
      setFilteredQuestions([]);
      return;
    }

    let tempLimit = 10;
    let found = false;
    let filtered = [];

    while (!found) {
      const { data: newData } = await refetch({
        courseId: course_id,
        page: 1,
        limit: tempLimit,
        categoryId: String(cat_id),
      });

      const allQuestions = newData?.getAllQuestion?.questions || [];

      filtered = allQuestions.filter(
        (q) =>
          q.title.toLowerCase().includes(term.toLowerCase()) ||
          q.text.toLowerCase().includes(term.toLowerCase())
      );

      if (filtered.length > 0 || tempLimit >= newData?.getAllQuestion?.totalCount) {
        found = true;
      } else {
        tempLimit += 10;
      }
    }

    setFilteredQuestions(filtered);
  };

  // Pagination logic
  const totalPages = data?.getAllQuestion?.totalCount
    ? Math.ceil(data.getAllQuestion.totalCount / questionsPerPage)
    : 1;

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  // Delete logic
  const handleDelete = async (id, title) => {
    try {
      const reason = await swal({
        text: `Enter reason for requesting deletion of question "${title}":`,
        content: "input",
        button: { text: "Submit", closeModal: false },
      });

      if (!reason) {
        swal.close();
        return;
      }

      const payload = {
        operationName: "deleteQuestion",
        query:
          'mutation deleteQuestion($id: String!, $course_id: String!) { deleteQuestion(id: $id, course_id: $course_id) }',
        variables: { id, course_id },
      };

      const user = JSON.parse(localStorage.getItem("user"));
      const userdisplayName = user?.displayName || "Unknown User";

      const response = await apolloClient.mutate({
        mutation: REQUEST_DELETION_MUTATION,
        variables: {
          typeOfDeletion: "QUESTION",
          payload,
          reasonForDeletion: reason,
          requestedBy: userdisplayName,
          title,
        },
      });

      if (response?.data?.requestDeletion) {
        swal(
          "Request Submitted",
          "Your delete request has been sent for admin approval.",
          "success"
        );
        refetch();
      } else {
        swal("Error", "Failed to submit delete request.", "error");
      }
    } catch (error) {
      swal("Error", "Something went wrong!", "error");
    }
  };

  if (loading || Isload) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader />
        </div>
      </AdminLayout>
    );
  }

  // Which list to display
  const displayList = searchTerm.trim() ? filteredQuestions : questions;

  return (
    <AdminLayout>
      <NetworkIndicator />
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => window.history.back()}
            className="text-gray-500 hover:text-gray-700 w-[100px]"
          >
            <span className="flex items-center gap-2 text-sm font-medium">
              <IoMdArrowRoundBack className="w-6 h-6" />
              Go Back
            </span>
          </button>

          <h2 className="text-3xl font-bold text-center max-md:text-2xl">
            {catName}
          </h2>

          {/* Search + Import */}
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={handleSearch}
              className="border border-gray-400 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 w-[200px]"
            />
            <Button className="p-0 bg-primary">
              <Link to="import-question" className="block px-2 py-1">
                Import Question
              </Link>
            </Button>
          </div>
        </div>

        {/* Questions */}
        <div className="p-5 bg-white rounded-md shadow-md">
          {displayList.length === 0 ? (
            <div className="text-center text-gray-500">No questions available</div>
          ) : (
            displayList.map((question, index) => (
              <div key={question?.id || index}>
                <Question
                  data={question}
                  courseId={course_id}
                  onDelete={() => handleDelete(question.id, question.title)}
                  catName={catName}
                  createdBy={question?.createdBy || "BT ADMIN"}   // ✅ Added Here
                />
                {index !== displayList.length - 1 && (
                  <hr className="px-8 my-4" />
                )}
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {!searchTerm.trim() && (
          <div className="flex justify-between mt-4">
            <Button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="p-0 bg-primary"
            >
              Previous
            </Button>
            <span className="text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="p-0 bg-primary"
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default QuestionListing;
